import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import type { Viewport } from './markupGeometry';
import { gesturePreview, type Gesture } from './markupGestures';
import type { MarkupShape } from './markupShapes';
import { drawScene, sceneShapes } from './renderMarkup';
import { drawSelection, selectionOverlay } from './renderSelection';
import { measureLabel } from './textMeasure';

export interface StageMarks {
    shapes: readonly MarkupShape[];
    selectedId: string | null;
    hoverId: string | null;
    // The label open in the text field; the field shows it, so the canvas skips it.
    hiddenId: string | null;
}

interface UseMarkupCanvasArgs {
    canvasRef: RefObject<HTMLCanvasElement>;
    image: ImageBitmap;
    viewport: Viewport;
    marks: StageMarks;
    gestureRef: RefObject<Gesture | null>;
}

function sizeCanvas(canvas: HTMLCanvasElement, viewport: Viewport, ratio: number): void {
    const width = Math.max(1, Math.round(viewport.width * ratio));
    const height = Math.max(1, Math.round(viewport.height * ratio));
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
}

function drawOverlays(
    ctx: CanvasRenderingContext2D,
    scene: readonly MarkupShape[],
    marks: StageMarks,
    scale: number
) {
    const selected = scene.find((shape) => shape.id === marks.selectedId);
    const hovered =
        marks.hoverId !== marks.selectedId && scene.find((shape) => shape.id === marks.hoverId);
    if (hovered)
        drawSelection(ctx, selectionOverlay(hovered, measureLabel, 1 / scale), scale, 'hover');
    if (selected)
        drawSelection(ctx, selectionOverlay(selected, measureLabel, 1 / scale), scale, 'selected');
}

function paint(
    canvas: HTMLCanvasElement,
    image: ImageBitmap,
    viewport: Viewport,
    marks: StageMarks,
    gesture: Gesture | null
): void {
    const ratio = window.devicePixelRatio || 1;
    sizeCanvas(canvas, viewport, ratio);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const preview = gesture ? gesturePreview(gesture) : null;
    const scene = sceneShapes(marks.shapes, preview, marks.hiddenId);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const scale = viewport.scale * ratio;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    drawScene(ctx, image, scene);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    drawOverlays(
        ctx,
        scene,
        gesture?.type === 'draw' ? { ...marks, hoverId: null } : marks,
        viewport.scale
    );
}

// Committed marks repaint through React; a live gesture repaints at most once per frame.
export function useMarkupCanvas({
    canvasRef,
    image,
    viewport,
    marks,
    gestureRef
}: UseMarkupCanvasArgs): () => void {
    const frameRef = useRef<number | null>(null);

    const redraw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || viewport.scale <= 0) return;
        paint(canvas, image, viewport, marks, gestureRef.current);
    }, [canvasRef, gestureRef, image, viewport, marks]);

    // A frame queued before a state change must paint the newest marks, not the ones it saw.
    const latestRedrawRef = useRef(redraw);
    useLayoutEffect(() => {
        latestRedrawRef.current = redraw;
        redraw();
    }, [redraw]);

    const scheduleRedraw = useCallback(() => {
        if (frameRef.current != null) return;
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null;
            latestRedrawRef.current();
        });
    }, []);

    useEffect(
        () => () => {
            if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
        },
        []
    );

    return scheduleRedraw;
}
